import {
  type Folder,
  type FolderId,
  type FolderSection,
  folderNameProblem,
  MAX_FOLDER_DEPTH,
  MAX_FOLDER_NAME_CHARS,
  normalizeFolderName,
} from "@esi/shared";
import { ArrowUpFromLine, ShieldCheck } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FolderPicker, pathText } from "./FolderPicker.tsx";
import { type FolderWriteResult, folderErrorText, useFolderWrite } from "./folders.ts";
import {
  type FolderNode,
  folderNodeId,
  folderPath,
  type ItemNode,
  type KnowledgeTree,
  subtreeHeight,
} from "./knowledge-tree.ts";

/** What a folder dialog acts on; null when closed. */
export type FolderDialog =
  | { kind: "create"; section: FolderSection; parent: FolderNode | null }
  | { kind: "rename"; folder: FolderNode }
  | { kind: "move"; node: FolderNode | ItemNode }
  | { kind: "delete"; folder: FolderNode };

/** The section a node's folders belong to (connected items have none). */
const sectionOf = (node: FolderNode | ItemNode): FolderSection =>
  node.section === "wiki" ? "wiki" : "files";

/**
 * The folder dialogs of the tree (design §7): create and rename (the name is visible to the
 * whole project), move to a folder of the same section through the picker, and delete with
 * one confirmation stating that the contents move up and nothing is deleted.
 */
export function FolderDialogs(props: {
  projectId: string;
  tree: KnowledgeTree;
  folders: readonly Folder[];
  dialog: FolderDialog | null;
  onClose: () => void;
  /** After a write: the node to focus and reveal, and what the API answered. */
  onDone: (result: FolderWriteResult) => void;
  returnFocus?: HTMLElement | null | (() => HTMLElement | null);
}) {
  const { dialog } = props;
  const common = {
    projectId: props.projectId,
    onClose: props.onClose,
    onDone: props.onDone,
    returnFocus: props.returnFocus,
  };
  return (
    <>
      <NameDialog
        {...common}
        tree={props.tree}
        dialog={dialog?.kind === "create" || dialog?.kind === "rename" ? dialog : null}
      />
      <MoveDialog
        {...common}
        tree={props.tree}
        folders={props.folders}
        node={dialog?.kind === "move" ? dialog.node : null}
      />
      <DeleteDialog
        {...common}
        tree={props.tree}
        folder={dialog?.kind === "delete" ? dialog.folder : null}
      />
    </>
  );
}

type Common = {
  projectId: string;
  tree: KnowledgeTree;
  onClose: () => void;
  onDone: (result: FolderWriteResult) => void;
  returnFocus?: HTMLElement | null | (() => HTMLElement | null);
};

function useSectionName() {
  const { t } = useTranslation();
  return (section: FolderSection) => t(`knowledgePanel.section.${section}`);
}

/** A folder's full path from its section ("Files › Manuals › Pumps"). */
function useFolderPathText(tree: KnowledgeTree) {
  const sectionName = useSectionName();
  return (section: FolderSection, folder: FolderNode | null) =>
    pathText(
      sectionName(section),
      folder ? [...folderPath(tree, folder), folder].map((f) => f.name) : [],
    );
}

function NameDialog(
  props: Common & {
    dialog: Extract<FolderDialog, { kind: "create" | "rename" }> | null;
  },
) {
  const { dialog } = props;
  const { t } = useTranslation();
  const id = useId();
  const write = useFolderWrite(props.projectId);
  const pathOf = useFolderPathText(props.tree);
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  const reset = write.reset;
  // Each opening starts from the folder's current name (rename) or empty (create).
  useEffect(() => {
    setName(dialog?.kind === "rename" ? dialog.folder.name : "");
    setTouched(false);
    reset();
  }, [dialog, reset]);
  const problem = folderNameProblem(normalizeFolderName(name));
  const close = () => {
    if (!write.isPending) {
      props.onClose();
    }
  };
  const submit = () => {
    setTouched(true);
    if (!dialog || problem || write.isPending) {
      return;
    }
    write.mutate(
      dialog.kind === "create"
        ? {
            op: "create",
            section: dialog.section,
            parentId: dialog.parent?.folder.id ?? null,
            name,
          }
        : { op: "rename", folderId: dialog.folder.folder.id, name },
      {
        onSuccess: (result) => {
          props.onClose();
          props.onDone(result);
        },
      },
    );
  };
  const where =
    dialog?.kind === "create"
      ? pathOf(dialog.section, dialog.parent)
      : dialog
        ? pathOf(dialog.folder.folder.section, null)
        : "";
  return (
    <AppDialog
      open={dialog !== null}
      onClose={close}
      returnFocus={props.returnFocus}
      title={
        dialog?.kind === "rename"
          ? t("folders.renameTitle", { name: dialog.folder.name })
          : t("folders.createTitle")
      }
      description={dialog?.kind === "create" ? t("folders.createIn", { path: where }) : undefined}
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-name`}>{t("folders.nameLabel")}</Label>
          <Input
            id={`${id}-name`}
            value={name}
            maxLength={MAX_FOLDER_NAME_CHARS * 4}
            autoComplete="off"
            aria-invalid={touched && problem !== null}
            aria-describedby={`${id}-note${touched && problem ? ` ${id}-problem` : ""}`}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          {touched && problem ? (
            <p id={`${id}-problem`} className="text-sm text-danger-fg">
              {t(`folders.nameProblem.${problem}`, { max: MAX_FOLDER_NAME_CHARS })}
            </p>
          ) : null}
          <p id={`${id}-note`} className="text-xs text-fg-muted">
            {t("folders.visibleNote")}
          </p>
        </div>
        {write.error ? <Notice tone="danger">{folderErrorText(write.error)}</Notice> : null}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={write.isPending} onClick={close}>
            {t("files.cancel")}
          </Button>
          <Button type="submit" disabled={write.isPending}>
            {dialog?.kind === "rename" ? t("folders.save") : t("folders.create")}
          </Button>
        </DialogFooter>
      </form>
    </AppDialog>
  );
}

function MoveDialog(
  props: Common & { folders: readonly Folder[]; node: FolderNode | ItemNode | null },
) {
  const { node } = props;
  const { t } = useTranslation();
  const write = useFolderWrite(props.projectId);
  const pathOf = useFolderPathText(props.tree);
  const current = (n: FolderNode | ItemNode) => {
    const parent = n.parentId ? props.tree.byId.get(n.parentId) : undefined;
    return parent?.kind === "folder" ? parent.folder.id : null;
  };
  // The choice belongs to one opening; until one is made it is the node's own folder, known on
  // the first render, so the picker mounts with it selected and revealed.
  const [choice, setChoice] = useState<{ node: FolderNode | ItemNode; target: FolderId | null }>();
  const target = node && choice?.node === node ? choice.target : node ? current(node) : null;
  const setTarget = (to: FolderId | null) => {
    if (node) {
      setChoice({ node, target: to });
    }
  };
  const reset = write.reset;
  useEffect(() => {
    if (node) {
      reset();
    }
  }, [node, reset]);
  const close = () => {
    if (!write.isPending) {
      props.onClose();
    }
  };
  const height = node?.kind === "folder" ? subtreeHeight(node) : 0;
  // Disabled targets keep their place in the tree, with the reason (design §7).
  const reason = (to: FolderNode | null): string | null => {
    if (!node) {
      return null;
    }
    const toId = to?.folder.id ?? null;
    if (toId === current(node)) {
      return t("folders.picker.here");
    }
    if (node.kind === "folder" && to) {
      if (to.id === node.id || folderPath(props.tree, to).some((f) => f.id === node.id)) {
        return t("folders.picker.inside");
      }
      if (to.depth + 1 + height > MAX_FOLDER_DEPTH) {
        return t("folders.picker.tooDeep", { max: MAX_FOLDER_DEPTH });
      }
    }
    return null;
  };
  const section = node ? sectionOf(node) : "files";
  const targetNode = target ? props.tree.byId.get(folderNodeId(target)) : undefined;
  // A chosen folder deleted meanwhile (the folders were read again): the root, as shown.
  const destination = targetNode?.kind === "folder" ? targetNode.folder.id : null;
  const blocked = node ? reason(targetNode?.kind === "folder" ? targetNode : null) : null;
  const submit = () => {
    // Artefacts belong to the conversation: never moved into folders.
    if (
      !node ||
      blocked ||
      write.isPending ||
      (node.kind === "item" && (node.type === "artefact" || node.type === "attachment"))
    ) {
      return;
    }
    write.mutate(
      node.kind === "folder"
        ? { op: "move", folderId: node.folder.id, parentId: destination }
        : {
            op: "moveItem",
            documentId: node.type === "wiki" ? node.page.id : node.document.id,
            section,
            folderId: destination,
          },
      {
        onSuccess: (result) => {
          props.onClose();
          props.onDone(result);
        },
      },
    );
  };
  return (
    <AppDialog
      open={node !== null}
      onClose={close}
      returnFocus={props.returnFocus}
      title={node ? t("folders.moveTitle", { name: node.name }) : ""}
      description={t("folders.moveLead")}
    >
      {node ? (
        <div className="flex flex-col gap-5">
          <FolderPicker
            projectId={props.projectId}
            folders={props.folders}
            section={section}
            value={target}
            onChange={setTarget}
            disabledReason={reason}
            canCreate
            label={t("folders.picker.label")}
          />
          <p className="text-sm wrap-anywhere text-fg-secondary">
            {t("folders.destination", {
              path: pathOf(section, targetNode?.kind === "folder" ? targetNode : null),
            })}
          </p>
          {write.error ? <Notice tone="danger">{folderErrorText(write.error)}</Notice> : null}
          <DialogFooter sticky>
            <Button variant="outline" disabled={write.isPending} onClick={close}>
              {t("files.cancel")}
            </Button>
            <Button disabled={write.isPending || blocked !== null} onClick={submit}>
              {t("folders.moveHere")}
            </Button>
          </DialogFooter>
        </div>
      ) : null}
    </AppDialog>
  );
}

function DeleteDialog(props: Common & { folder: FolderNode | null }) {
  const { folder } = props;
  const { t } = useTranslation();
  const write = useFolderWrite(props.projectId);
  const sectionName = useSectionName();
  const reset = write.reset;
  useEffect(() => {
    if (folder) {
      reset();
    }
  }, [folder, reset]);
  const close = () => {
    if (!write.isPending) {
      props.onClose();
    }
  };
  const parent = folder?.parentId ? props.tree.byId.get(folder.parentId) : undefined;
  const parentName =
    parent?.kind === "folder" ? parent.name : folder ? sectionName(folder.folder.section) : "";
  // What the viewer sees directly inside: the counts never include hidden items.
  const items = folder?.children.filter((c) => c.kind === "item").length ?? 0;
  const subfolders = folder?.children.filter((c) => c.kind === "folder").length ?? 0;
  const itemsText = t(
    folder?.folder.section === "wiki" ? "folders.delete.pages" : "folders.delete.files",
    { count: items },
  );
  const foldersText = t("folders.delete.folders", { count: subfolders });
  return (
    <AppDialog
      open={folder !== null}
      onClose={close}
      returnFocus={props.returnFocus}
      title={folder ? t("folders.delete.title", { name: folder.name }) : ""}
      description={t("folders.delete.lead")}
    >
      {folder ? (
        <div className="flex flex-col gap-5">
          <ul className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface-raised text-sm">
            <li className="flex items-start gap-3 px-3 py-2.5">
              <ArrowUpFromLine
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-fg-muted"
                strokeWidth={1.75}
              />
              <span className="text-fg">
                {items + subfolders === 0
                  ? t("folders.delete.empty", { parent: parentName })
                  : items > 0 && subfolders > 0
                    ? t("folders.delete.movesBoth", {
                        items: itemsText,
                        folders: foldersText,
                        parent: parentName,
                      })
                    : t("folders.delete.moves", {
                        what: items > 0 ? itemsText : foldersText,
                        parent: parentName,
                      })}
              </span>
            </li>
            <li className="flex items-start gap-3 px-3 py-2.5">
              <ShieldCheck
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-fg-muted"
                strokeWidth={1.75}
              />
              <span className="text-fg-secondary">{t("folders.delete.nothing")}</span>
            </li>
          </ul>
          {write.error ? <Notice tone="danger">{folderErrorText(write.error)}</Notice> : null}
          <DialogFooter>
            <Button variant="outline" disabled={write.isPending} onClick={close}>
              {t("files.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={write.isPending}
              onClick={() =>
                write.mutate(
                  { op: "delete", folderId: folder.folder.id },
                  {
                    onSuccess: (result) => {
                      props.onClose();
                      props.onDone(result);
                    },
                  },
                )
              }
            >
              {t("folders.delete.confirm")}
            </Button>
          </DialogFooter>
        </div>
      ) : null}
    </AppDialog>
  );
}
